var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import { MissingComponent } from './MissingComponent';
import { EditMode, isDynamicPlaceholder, getDynamicPlaceholderPattern, } from '@sitecore-jss/sitecore-jss/layout';
import { constants } from '@sitecore-jss/sitecore-jss';
import { convertAttributesToReactProps } from '../utils';
import { HiddenRendering } from './HiddenRendering';
import { FEaaSComponent, FEAAS_COMPONENT_RENDERING_NAME } from './FEaaSComponent';
import { FEaaSWrapper, FEAAS_WRAPPER_RENDERING_NAME } from './FEaaSWrapper';
import { BYOCComponent, BYOC_COMPONENT_RENDERING_NAME } from './BYOCComponent';
import { BYOCWrapper, BYOC_WRAPPER_RENDERING_NAME } from './BYOCWrapper';
import { PlaceholderMetadata } from './PlaceholderMetadata';
import ErrorBoundary from './ErrorBoundary';
export class PlaceholderCommon extends React.Component {
    constructor(props) {
        super(props);
        this.nodeRefs = [];
        this.state = {};
        this.addRef = this.addRef.bind(this);
        this.updateKeyAttributes = this.updateKeyAttributes.bind(this);
        this.createRawElement = this.createRawElement.bind(this);
    }
    static getPlaceholderDataFromRenderingData(rendering, name, editMode) {
        let result;
        let phName = name.slice();
        /**
         * Process (SXA) dynamic placeholders
         * Find and replace the matching dynamic placeholder e.g 'nameOfContainer-{*}' with the requested e.g. 'nameOfContainer-1'.
         * For Metadata EditMode, we need to keep the raw placeholder name in place.
         */
        if (rendering === null || rendering === void 0 ? void 0 : rendering.placeholders) {
            Object.keys(rendering.placeholders).forEach((placeholder) => {
                const patternPlaceholder = isDynamicPlaceholder(placeholder)
                    ? getDynamicPlaceholderPattern(placeholder)
                    : null;
                if (patternPlaceholder && patternPlaceholder.test(phName)) {
                    if (editMode === EditMode.Metadata) {
                        phName = placeholder;
                    }
                    else {
                        rendering.placeholders[phName] = rendering.placeholders[placeholder];
                        delete rendering.placeholders[placeholder];
                    }
                }
            });
        }
        if (rendering && rendering.placeholders && Object.keys(rendering.placeholders).length > 0) {
            result = rendering.placeholders[phName];
        }
        else {
            result = null;
        }
        if (!result) {
            console.warn(`Placeholder '${phName}' was not found in the current rendering data`, JSON.stringify(rendering, null, 2));
            return [];
        }
        return result;
    }
    componentDidMount() {
        this.updateKeyAttributes();
    }
    componentDidUpdate() {
        this.updateKeyAttributes();
    }
    componentDidCatch(error) {
        this.setState({ error });
    }
    getSXAParams(rendering) {
        if (!rendering.params)
            return {};
        return (rendering.params.FieldNames && {
            styles: `${rendering.params.GridParameters || ''} ${rendering.params.Styles || ''}`,
        });
    }
    getComponentsForRenderingData(placeholderData) {
        var _a;
        const _b = this.props, { name, fields: placeholderFields, params: placeholderParams, missingComponentComponent, hiddenRenderingComponent } = _b, placeholderProps = __rest(_b, ["name", "fields", "params", "missingComponentComponent", "hiddenRenderingComponent"]);
        const transformedComponents = placeholderData
            .map((rendering, index) => {
            var _a, _b;
            const key = rendering.uid
                ? rendering.uid
                : `component-${index}`;
            const commonProps = { key };
            let isEmpty = false;
            // if the element is not a 'component rendering', render it 'raw'
            if (!rendering.componentName &&
                rendering.name) {
                return this.createRawElement(rendering, commonProps);
            }
            const componentRendering = rendering;
            let component;
            if (componentRendering.componentName === constants.HIDDEN_RENDERING_NAME) {
                component = hiddenRenderingComponent !== null && hiddenRenderingComponent !== void 0 ? hiddenRenderingComponent : HiddenRendering;
                isEmpty = true;
            }
            else if (!componentRendering.componentName) {
                component = () => React.createElement(React.Fragment, null);
                isEmpty = true;
            }
            else {
                component = this.getComponentForRendering(componentRendering);
            }
            // Fallback/defaults for Sitecore Component renderings (in case not defined in component factory)
            if (!component) {
                if (componentRendering.componentName === FEAAS_COMPONENT_RENDERING_NAME) {
                    component = FEaaSComponent;
                }
                else if (componentRendering.componentName === FEAAS_WRAPPER_RENDERING_NAME) {
                    component = FEaaSWrapper;
                }
                else if (componentRendering.componentName === BYOC_COMPONENT_RENDERING_NAME) {
                    component = BYOCComponent;
                }
                else if (componentRendering.componentName === BYOC_WRAPPER_RENDERING_NAME) {
                    component = BYOCWrapper;
                }
            }
            if (!component) {
                console.error(`Placeholder ${name} contains unknown component ${componentRendering.componentName}. Ensure that a React component exists for it, and that it is registered in your componentFactory.js.`);
                component = missingComponentComponent !== null && missingComponentComponent !== void 0 ? missingComponentComponent : MissingComponent;
                isEmpty = true;
            }
            const finalProps = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, commonProps), placeholderProps), ((placeholderFields || componentRendering.fields) && {
                fields: Object.assign(Object.assign({}, placeholderFields), componentRendering.fields),
            })), ((placeholderParams || componentRendering.params) && {
                params: Object.assign(Object.assign(Object.assign({}, placeholderParams), componentRendering.params), this.getSXAParams(componentRendering)),
            })), { rendering: componentRendering });
            let rendered = React.createElement(component, this.props.modifyComponentProps ? this.props.modifyComponentProps(finalProps) : finalProps);
            if (!isEmpty) {
                // assign type based on passed element - type='text/sitecore' should be ignored when renderEach Placeholder prop function is being used
                const type = rendered.props.type === 'text/sitecore' ? rendered.props.type : '';
                // wrapping with error boundary could cause problems in case where parent component uses withPlaceholder HOC and tries to access its children props
                // that's why we need to expose element's props here
                rendered = (React.createElement(ErrorBoundary, Object.assign({ key: rendered.type + '-' + index, errorComponent: this.props.errorComponent, componentLoadingMessage: this.props.componentLoadingMessage, type: type, isDynamic: ((_a = component.render) === null || _a === void 0 ? void 0 : _a.preload) ? true : false }, rendered.props), rendered));
            }
            // if editMode is equal to 'metadata' then emit shallow chromes for hydration in Pages
            if (((_b = this.props.sitecoreContext) === null || _b === void 0 ? void 0 : _b.editMode) === EditMode.Metadata) {
                return (React.createElement(PlaceholderMetadata, { key: key, rendering: rendering }, rendered));
            }
            return rendered;
        })
            .filter((element) => element); // remove nulls
        if (((_a = this.props.sitecoreContext) === null || _a === void 0 ? void 0 : _a.editMode) === EditMode.Metadata) {
            return [
                React.createElement(PlaceholderMetadata, { key: this.props.rendering.uid, placeholderName: name, rendering: this.props.rendering }, transformedComponents),
            ];
        }
        return transformedComponents;
    }
    getComponentForRendering(renderingDefinition) {
        var _a;
        const componentFactory = this.props.componentFactory;
        if (!componentFactory || typeof componentFactory !== 'function') {
            console.warn(`No componentFactory was available to service request for component ${renderingDefinition}`);
            return null;
        }
        // Render SXA Rendering Variant
        if ((_a = renderingDefinition.params) === null || _a === void 0 ? void 0 : _a.FieldNames) {
            return componentFactory(renderingDefinition.componentName, renderingDefinition.params.FieldNames);
        }
        return componentFactory(renderingDefinition.componentName);
    }
    addRef(nodeRef) {
        this.nodeRefs.push(nodeRef);
    }
    createRawElement(elem, baseProps) {
        if (!elem.name) {
            console.error('"elem.name" is undefined in "createRawElement". Something is likely wrong with your component data. Ensure that your components have a name.');
            return null;
        }
        const attributes = convertAttributesToReactProps(elem.attributes);
        const props = Object.assign(Object.assign(Object.assign({}, baseProps), attributes), { dangerouslySetInnerHTML: elem.contents ? { __html: elem.contents } : undefined });
        /* Since we can't set the "key" attribute via React, stash it
         * so we can set in the DOM after render.
         */
        if (!Array.isArray(attributes) && attributes && attributes.chrometype === 'placeholder') {
            props.phkey = elem.attributes.key; // props that get rendered as dom attribute names need to be lowercase, otherwise React complains.
            props.ref = this.addRef; // only need ref for placeholder containers, trying to add it to other components (e.g. stateless components) may result in a warning.
        }
        return React.createElement(elem.name, props);
    }
    updateKeyAttributes() {
        if (!this.nodeRefs) {
            return;
        }
        this.nodeRefs.forEach((ref) => {
            if (ref && ref.getAttribute) {
                // ref might be a wrapped component, so check for existence of getAttribute method
                const key = ref.getAttribute('phkey');
                if (key) {
                    // need to manually set the 'key' attribute after component mount because
                    // 'key' is a reserved attribute/prop in React. so it will never be rendered
                    // as an html attribute.
                    ref.setAttribute('key', key);
                }
            }
        });
    }
}
PlaceholderCommon.propTypes = {
    rendering: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.object,
    ]).isRequired,
    fields: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.object,
    ]).isRequired),
    params: PropTypes.objectOf(PropTypes.string.isRequired),
    missingComponentComponent: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
    ]),
    hiddenRenderingComponent: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
    ]),
    errorComponent: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
    ]),
    modifyComponentProps: PropTypes.func,
    sitecoreContext: PropTypes.object,
};
